import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector, useStore } from 'react-redux';
import io from 'socket.io-client';
import { buildSpecsFromHeaters } from 'shared/helpers/buildSpecsFromHeaters';
import { useQueryClient } from 'react-query';
import { graphQueryKey } from 'shared/hooks/useRealtimeGraphData';
import { ssrStateKey } from 'shared/hooks/useSsrState';
import { thermocoupleSettingKey } from 'shared/hooks/useReceivedThermocoupleSetting';
import {
  applySsrSocketSpecs,
  applySsrSocketThermocouple,
} from 'shared/helpers/ssrCache';
import { applyThermocoupleSetting } from 'shared/helpers/thermocoupleCache';
import { useSsrElementsBank } from 'shared/hooks/useSsrElementsBank';
import {
  handleAddHeatingSchedule,
  handleClearHeatingSchedule,
  handleEssSSRStateSocket,
  handleEssSwitchSocket,
  selectEssSwitch,
} from 'shared/slice/essSwitchSlice';
import { selectUnits } from 'shared/slice/unitsSlice';
import {
  handleTesSwitchSocket,
  handleTesSSRStateSocket,
  selectTesSwitch,
  tesHandleClearHeatingSchedule,
  tesHandleAddHeatingSchedule,
} from 'shared/slice/tesSwitchSlice';
import {
  handleTgsSwitchSocket,
  tgsHandleAddHeatingSchedule,
  tgsHandleClearHeatingSchedule,
} from 'shared/slice/tgsSwitchSlice';
import {
  convertCelsiusToFahrenheit,
  getGraphTypeKey,
  readableTime,
} from 'shared/helpers/helpers';




const getSocketUrl = () => {
  try {
    return (
      process.env.REACT_APP_SOCKET_URL ||
      new URL(process.env.REACT_APP_BASE_URL).origin
    );
  } catch (error) {
    return 'https://api.dev.umb-360.com';
  }
};

export const useSocket = (room, accessToken) => {
  const [socket, setSocket] = useState(null);
  const dispatch = useDispatch();
  const store = useStore();
  const queryClient = useQueryClient();
  const { elementsOptions } = useSsrElementsBank({ enabled: Boolean(accessToken) });
  const { isF } = useSelector(selectUnits);





  const writeSsrSocketCache = (data, swtName) => {
    const location = data?.zoneInfo?.zone_id;
    const machine = data?.device_mac || data?.device_id;
    if (!location || !machine) return;
    const state = store.getState();
    const flat =
      swtName === 'ESS'
        ? selectEssSwitch(state)?.flatEssSwitch
        : selectTesSwitch(state)?.flatTesSwitch;
    const map = flat?.[location]?.[machine]?.heaterThermocoupleMap || [];
    queryClient.setQueryData(ssrStateKey(machine), (prev) => {
      const prevState = prev || {};
      if (Object.prototype.hasOwnProperty.call(data, 'specs')) {
        return applySsrSocketSpecs(prevState, data, map);
      }
      if (data.heater_thermocouple_map) {
        return applySsrSocketThermocouple(prevState, data.heater_thermocouple_map);
      }
      return prevState;
    });
  };





  const latestRef = useRef({ elementsOptions, isF });
  latestRef.current = { elementsOptions, isF };

  useEffect(() => {
    if (accessToken) {
      const socketUrl = getSocketUrl();
      const newSocket = io(socketUrl, {
        query: { room }
      });


      newSocket.on('connect', () => {
        console.log('WebSocket connection established.', socketUrl);
      });

      newSocket.on('connect_error', (e) => {
        console.log('WebSocket connection error.', e);
      })


      newSocket.on('switchAudit', (data) => {
        const { isF } = latestRef.current;
        dispatch(handleEssSwitchSocket({ data, isF }));
        writeSsrSocketCache(data, 'ESS');
        dispatch(handleEssSSRStateSocket(data));
      });

      newSocket.on('blowerAudit', (data) => {
        const { isF } = latestRef.current;
        if (data.eventDeviceType === 'TGS') {
          dispatch(handleTgsSwitchSocket({ data, isF }));
        } else if (data.eventDeviceType === 'TES') {
          dispatch(handleTesSwitchSocket({ data, isF }));
          writeSsrSocketCache(data, 'TES');
          dispatch(handleTesSSRStateSocket(data));
        }
      });

      newSocket.on('ssrUpdate', (data) => {
        const { elementsOptions } = latestRef.current;
        const specs = buildSpecsFromHeaters(data.Heaters, elementsOptions);
        const newData = {
          ...data,
          specs: specs.length !== 0 ? specs : [{}],
        }
        if (data.eventDeviceType === 'ESS') {
          writeSsrSocketCache(newData, 'ESS');
          dispatch(handleEssSSRStateSocket(newData));
        } else if (data.eventDeviceType === 'TES') {
          writeSsrSocketCache(newData, 'TES');
          dispatch(handleTesSSRStateSocket(newData));
        }
      });

      newSocket.on('scheduleList', (data) => {
        const { isF } = latestRef.current;
        const location = data.zoneInfo.zone_id;
        const machine = data.device_mac;
        const clearData = [
          {
            start: { date: null, time: null },
            end: { date: null, time: null },
            inputTemp: null,
            isF: null,
            id: null,
          },
        ];

        const addSchedule = data.eventDeviceType === 'ESS'
          ? handleAddHeatingSchedule
          : data.eventDeviceType === 'TES'
            ? tesHandleAddHeatingSchedule
            : tgsHandleAddHeatingSchedule;

        const clearSchedule = data.eventDeviceType === 'ESS'
          ? handleClearHeatingSchedule
          : data.eventDeviceType === 'TES'
            ? tesHandleClearHeatingSchedule
            : tgsHandleClearHeatingSchedule;

        if (data.hasOwnProperty('threshold')) {
          const { threshold, startDate, endDate, id } = data;
          dispatch(addSchedule({
            location,
            machine,
            start: readableTime(startDate),
            end: readableTime(endDate),
            index: 0,
            inputTemp: isF ? convertCelsiusToFahrenheit(+threshold) : +threshold,
            isF,
            id,
          }));
        } else {
          dispatch(clearSchedule({ location, machine, data: clearData }));
        }
      });

      newSocket.on('thermocoupleUpdate', (data) => {
        const swtName = data.eventDeviceType.toLowerCase();



        queryClient.setQueryData(
          thermocoupleSettingKey(data.device_id),
          (old) => applyThermocoupleSetting(old, { ...data, deviceType: swtName })
        );
      });

      const handleGraph = (data, graphType) => {


        queryClient.setQueryData(
          graphQueryKey(data.device_mac, getGraphTypeKey(graphType)),
          data.points ?? []
        );
      };

      newSocket.on('heater_graph', (data) => handleGraph(data, 'heater_graph'));
      newSocket.on('enclosure_graph', (data) => handleGraph(data, 'enclosure_graph'));
      newSocket.on('gas_enclosure_graph', (data) => handleGraph(data, 'gas_enclosure_graph'));
      newSocket.on('outside_graph', (data) => handleGraph(data, 'outside_graph'));
      newSocket.on('gas_graph', (data) => handleGraph(data, 'gas_graph'));
      newSocket.on('snow_graph', (data) => handleGraph(data, 'snow_graph'));
      newSocket.on('wind_graph', (data) => handleGraph(data, 'wind_graph'));
      newSocket.on('gas_snow_graph', (data) => handleGraph(data, 'gas_snow_graph'));
      newSocket.on('gas_wind_graph', (data) => handleGraph(data, 'gas_wind_graph'));


      newSocket.on('disconnect', () => {
        console.log('WebSocket connection closed.');
      });





      if (typeof window !== 'undefined' && window.__E2E__) {
        window.__emitSocket = (event, data) =>
          newSocket.listeners(event).forEach((fn) => fn(data));
      }

      setSocket(newSocket);


      return () => {
        if (typeof window !== 'undefined') delete window.__emitSocket;
        newSocket.disconnect();
      };
    }



  }, [room, accessToken]);

  const sendMessage = (message) => {
    if (socket && socket.connected) {
      socket.send(message);
    } else {
      console.log('WebSocket connection is not open yet.');
    }
  };

  return { sendMessage };
};
